import { Container, Sprite, Texture } from 'pixi.js';
import type { RocketBlockerModel } from 'game/boardMVC/models/blockers/RocketBlockerModel';
import { ROCKET_FLY_DURATION } from 'game/boardMVC/models/blockers/RocketBlockerModel';
import { gridPositionToPxPosition } from 'game/boardMVC/utils/gridPositionToPxPosition';
import last from 'lodash/last';
import type { BlockerView } from '../../types';
import { RocketBlockerLayout } from '../../../../../layouts/blockers/RocketBlockerLayout';
import type { TileStackView } from '../board/TileStackView';
import type { RocketBlockerRocketLayout } from '../../../../../layouts/blockers/RocketBlockerRocketLayout';
import { TILE_Z_OFFSET } from '../board/TileView';
import { soundManager } from '../../../../app/SoundManager';
import type { TileStackModel } from '../../models/board/TileStackModel';

type RocketReferences = {
  layout: RocketBlockerRocketLayout;
  mask: Sprite;
};

const ROCKET_Z_INDEX = 1000000;

const CANISTERS_HIT_ANIMATION_DURATION = 200;
const FLAME_ANIMATION_DURATION = 300;
const SMOKE_ANIMATION_DURATION = 300;
const EXPLOSION_ANIMATION_DURATION = 150;
const ROCKET_OUT_ANIMATION_DURATION = ROCKET_FLY_DURATION * 0.7;
const ROCKET_IN_ANIMATION_DURATION = ROCKET_FLY_DURATION * 0.3;

export class RocketBlockerView extends Container implements BlockerView {
  private readonly layout: RocketBlockerLayout;

  private hp = 4;

  private readonly rockets: RocketReferences[];

  constructor(
    private readonly model: RocketBlockerModel,
    private readonly stackView: TileStackView,
  ) {
    super();
    this.layout = new RocketBlockerLayout();
    this.layout.root.props({
      parent: this,
    });

    const { rocketBottom, rocketLeft, rocketRight, rocketTop, maskBottom, maskLeft, maskRight, maskTop } = this.layout;

    this.rockets = [
      {
        layout: rocketBottom,
        mask: maskBottom,
      },
      {
        layout: rocketLeft,
        mask: maskLeft,
      } as RocketReferences,
      {
        layout: rocketRight,
        mask: maskRight,
      } as RocketReferences,
      {
        layout: rocketTop,
        mask: maskTop,
      } as RocketReferences,
    ];

    this.rockets.forEach((ref) => {
      const {
        layout: { rocket, flame },
        mask,
      } = ref;

      rocket.mask = mask;
      rocket.setMask({ inverse: true });
      flame.alpha = 0;
    });

    this.setHp(model.getHp());
  }

  public getModel(): RocketBlockerModel {
    return this.model;
  }

  public onModelChanged(): void {
    if (this.hp - 1 === this.model.getHp() && this.model.targets.length > 0) {
      void this.animateHit(last(this.model.targets) ?? null);
    } else {
      this.setHp(this.model.getHp());
    }
  }

  public async animateRemoval(): Promise<void> {
    await this.tweenTo({ alpha: 0 }, ROCKET_FLY_DURATION, 'sinIn', true).asyncOnComplete();
    this.destroy({ children: true });
  }

  private setHp(hp: number): void {
    this.hp = Math.min(this.model.maxHp, Math.max(0, hp));

    const rocketIndex = this.model.maxHp - this.hp;

    for (let i = 0; i < this.rockets.length; i += 1) {
      const {
        layout: { root: container },
      } = this.rockets[i];

      container.visible = rocketIndex <= i;
    }

    this.hp = hp;
  }

  private async animateHit(target: TileStackModel | null): Promise<void> {
    const rocketIndex = this.model.maxHp - this.hp;
    this.hp -= 1;

    void this.animateCanistersHit();

    await this.animateRocketOut(this.rockets[rocketIndex]);
    await this.animateRocketIn(this.rockets[rocketIndex], target);
  }

  private async animateCanistersHit(): Promise<void> {
    const {
      layout: { whiteOverlay, canisters },
    } = this;

    await Promise.all([
      whiteOverlay
        .tweenTo({ alpha: 1 }, CANISTERS_HIT_ANIMATION_DURATION, 'quadOut', true, 0, 0, true)
        .asyncOnComplete(),
      canisters
        .tweenTo({ scaleY: 0.8 }, CANISTERS_HIT_ANIMATION_DURATION, 'quadOut', true, 0, 0, true)
        .asyncOnComplete(),
      canisters
        .tweenTo({ scaleX: 1.4 }, CANISTERS_HIT_ANIMATION_DURATION, 'quadOut', true, 0, 0, true)
        .asyncOnComplete(),
    ]);
  }

  private async animateRocketOut(ref: RocketReferences): Promise<void> {
    const {
      layout: { flame, root: container },
    } = ref;

    const totalDistance = container.getGlobalPosition().y + 100;

    // smoke on launch
    const smoke = new Sprite();
    smoke.props({
      parent: this.layout.root,
      texture: Texture.from('Rocket_Smoke'),
      anchor: 0.5,
      x: container.x,
      y: container.y,
      scaleX: 0.1,
      scaleY: 0.1,
      alpha: 0,
    });

    smoke.tweenTo({ scaleX: 0.5, scaleY: 0.5 }, SMOKE_ANIMATION_DURATION, 'quadOut', true);
    smoke.tweenTo({ alpha: 1 }, SMOKE_ANIMATION_DURATION, 'quadOut', true, 0, 0, true).onComplete.addOnce(() => {
      smoke.destroy();
    });

    flame.scale = { x: 0.3, y: 0.3 };

    // rocket up out of the canister
    const board = this.stackView.parent;
    if (!board) return;

    const globalContainerPosition = container.getGlobalPosition();
    container.removeFromParent();
    container.props({
      parent: board,
      position: board.toLocal(globalContainerPosition),
      zIndex: ROCKET_Z_INDEX,
    });

    soundManager.play('Gameplay_Rocket_Launch');

    await Promise.all([
      container
        .tweenTo({ y: container.y - totalDistance }, ROCKET_OUT_ANIMATION_DURATION, 'linear', true)
        .asyncOnComplete(),
      container.tweenTo({ scaleX: 1.4, scaleY: 1.4 }, ROCKET_OUT_ANIMATION_DURATION, 'linear', true).asyncOnComplete(),

      flame.tweenTo({ alpha: 1 }, FLAME_ANIMATION_DURATION, 'bounceOut', true).asyncOnComplete(),
      flame.tweenTo({ scaleX: 1, scaleY: 1 }, FLAME_ANIMATION_DURATION, 'linear', true).asyncOnComplete(),
    ]);
  }

  private async animateRocketIn(ref: RocketReferences, target: TileStackModel | null): Promise<void> {
    const {
      layout: { rocket, root: container },
      mask,
    } = ref;

    rocket.mask = null;
    rocket.position = { x: 0, y: 0 };
    mask.destroy();

    // define target position based on model.target (either a stack of tiles, a blocker or an empty space)
    let targetPosition = { x: 0, y: 0 };

    if (target) {
      targetPosition = gridPositionToPxPosition(target.x, target.y);
    } else {
      console.error('RocketBlockerView: target null');
      container.destroy({ children: true });
      return;
    }

    container.props({
      x: targetPosition.x,
      angle: 180,
      scaleX: 2.2,
    });

    // additional stack offset based on the amount of tiles in the target stack
    const tileOffsetY = target.getTileCount() * TILE_Z_OFFSET;
    const targetY = targetPosition.y - tileOffsetY;

    // fly through target
    await Promise.all([
      container.tweenTo({ y: targetY }, ROCKET_IN_ANIMATION_DURATION, 'linear', true).asyncOnComplete(),
      container.tweenTo({ scaleX: 1, scaleY: 1 }, ROCKET_IN_ANIMATION_DURATION, 'linear', true).asyncOnComplete(),
      container
        .tweenTo(
          { alpha: 0.5 },
          ROCKET_IN_ANIMATION_DURATION * 0.05,
          'linear',
          true,
          ROCKET_IN_ANIMATION_DURATION * 0.95,
        )
        .asyncOnComplete(),
    ]);

    container.destroy({ children: true });

    // animate detonation
    const board = this.stackView.parent;
    const explosionImpact = new Sprite();
    explosionImpact.props({
      parent: board,
      zIndex: ROCKET_Z_INDEX,
      texture: Texture.from('Rocket_Impact'),
      anchor: 0.5,
      x: targetPosition.x,
      y: targetY,
      scaleX: 0.1,
      scaleY: 0.1,
      alpha: 0,
    });

    soundManager.play('Gameplay_Rocket_Explosion');

    explosionImpact.tweenTo({ scaleX: 1, scaleY: 1 }, EXPLOSION_ANIMATION_DURATION, 'quadOut', true);
    explosionImpact
      .tweenTo({ alpha: 1 }, EXPLOSION_ANIMATION_DURATION, 'quadOut', true, 0, 0, true)
      .onComplete.addOnce(() => {
        explosionImpact.destroy();
      });

    const explosionSmoke = new Sprite();
    explosionSmoke.props({
      parent: board,
      zIndex: ROCKET_Z_INDEX,
      texture: Texture.from('Rocket_Smoke'),
      anchor: 0.5,
      x: targetPosition.x,
      y: targetY,
      scaleX: 0.1,
      scaleY: 0.1,
      alpha: 0.3,
    });

    explosionSmoke.tweenTo({ scaleX: 1, scaleY: 1 }, SMOKE_ANIMATION_DURATION * 2, 'linear', true);
    explosionSmoke
      .tweenTo({ alpha: 1 }, SMOKE_ANIMATION_DURATION, 'quadOut', true, 0, 0, true)
      .onComplete.addOnce(() => {
        explosionSmoke.destroy();
      });
  }
}
